import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  IEvaluationItem,
  IEvaluationItemBody,
  IEvaluationItemCreate,
} from '../../../models/evaluationItem.model';
import { YN } from '../../../models/common.api.model';
import {
  Button as AntButton,
  Descriptions,
  Modal,
  Table,
  TableColumnsType,
  Select,
} from 'antd';
import Button from '../../Button';
import Radio from '../../Radio';
import { IType } from '../../../models/evaluation.model';
import { ISubject } from '../../../models/subject.model';
import useNotification from '../../../hooks/useNotification';

const { Option } = Select;

interface IProps {
  data?: IEvaluationItem[];
  types: IType[];
  subjects: ISubject[];
  onUpdate: (evaluationItemCode: number, body: IEvaluationItemBody) => void;
  onCreate: (body: IEvaluationItemCreate) => void;
}

const Level2Content: FC<IProps> = ({
  data,
  types,
  subjects,
  onUpdate,
  onCreate,
}) => {
  const { t, i18n } = useTranslation();
  const { infoNotification } = useNotification();

  const [visible, setVisible] = useState<boolean>(false);
  const [evaluationItemName, setEvaluationItemName] = useState<string>('');
  const [memo, setMemo] = useState('');
  const [useYN, setUseYN] = useState<YN>('Y');
  const [evaluationTypeCode, setEvaluationTypeCode] = useState<number | null>();
  const [subjectCode, setSubjectCode] = useState<number | null>();
  const [evaluationItemCode, setEvaluationItemCode] = useState<number>();

  const columns: TableColumnsType<IEvaluationItem> = [
    {
      title: t('program.criteria.column6'),
      dataIndex: 'evaluationItemName',
    },
    {
      title: t('program.criteria.column2'),
      dataIndex: 'subjectName',
    },
    {
      title: t('program.criteria.column3'),
      dataIndex: 'evaluationTypeName',
      render: (value, record) =>
        i18n.language === 'ko' ? value : record.evaluationTypeNameEn,
    },
    {
      title: t('program.criteria.column4'),
      dataIndex: 'useYN',
    },
    {
      title: '',
      dataIndex: 'evaluationItemCode',
      render: (_, record) => (
        <div className="comm-btn__area justify-content-center">
          <Button
            className="btn-basic small pink-line"
            text={t('common.button.edit')}
            onClick={() => showModal(record)}
          />
        </div>
      ),
    },
  ];

  const showModal = (record?: IEvaluationItem) => {
    setVisible(true);
    setEvaluationItemCode(record?.evaluationItemCode);
    setEvaluationItemName(record?.evaluationItemName ?? '');
    setMemo(record?.memo ?? '');
    setUseYN(record?.useYN ?? 'Y');
    setSubjectCode(record?.subjectCode);
    setEvaluationTypeCode(record?.evaluationTypeCode);
  };

  const closeModal = () => {
    setVisible(false);
    setEvaluationItemCode(undefined);
  };

  const onSubmit = () => {
    if (!data) return;
    if (!evaluationItemName)
      return infoNotification(t('program.criteria.message1'));
    if (!evaluationTypeCode)
      return infoNotification(t('program.criteria.message2'));
    if (!subjectCode) return infoNotification(t('program.criteria.message3'));
    if (evaluationItemCode) {
      onUpdate(evaluationItemCode, { evaluationItemName, memo, useYN });
    } else {
      onCreate({
        evaluationItemName,
        memo,
        useYN,
        evaluationTypeCode,
        subjectCode,
      });
    }

    closeModal();
  };

  return (
    <>
      <div className="comm-bx__space">
        <div className="comm-btn__area right">
          <Button
            className="btn-basic pink"
            text={t('program.award.button2')}
            onClick={() => showModal()}
          />
        </div>
      </div>
      <Table
        className='comm-table__area'
        dataSource={data}
        columns={columns}
        pagination={false}
        rowKey="evaluationItemCode"
      />
      <Modal
        className="select-agent-modal"
        title={t('program.criteria.title2')}
        visible={visible}
        onCancel={closeModal}
        footer={[
          <div className='comm-btn__area justify-content-center'>
            <AntButton key="submit" className='btn-basic pink' onClick={onSubmit}>
              {t('common.button.save')}
            </AntButton>
            <AntButton key="cancel" className='btn-basic gray' onClick={closeModal}>
              {t('common.button.cancel')}
            </AntButton>
          </div>
        ]}
      >
        <div className="comm-table__area rowType mt-0">
          <Descriptions bordered column={1} size="small">
            <Descriptions.Item label={t('program.criteria.column1')}>
              <input
                type="text"
                className="form-control input-text"
                value={evaluationItemName}
                onChange={(e) => setEvaluationItemName(e.target.value)}
              />
            </Descriptions.Item>
            <Descriptions.Item label={t('program.criteria.column3')}>
              <Select
                onChange={setEvaluationTypeCode}
                style={{ width: '100%' }}
                disabled={Boolean(evaluationItemCode)}
                value={evaluationTypeCode}
              >
                {types.map(({ typeCode, typeName, typeNameEn }) => (
                  <Option value={typeCode}>
                    {i18n.language === 'ko' ? typeName : typeNameEn}
                  </Option>
                ))}
              </Select>
            </Descriptions.Item>
            <Descriptions.Item label={t('program.criteria.column2')}>
              <Select
                onChange={setSubjectCode}
                style={{ width: '100%' }}
                disabled={Boolean(evaluationItemCode)}
                value={subjectCode}
              >
                {subjects.map(({ subjectCode, subjectName }) => (
                  <Option value={subjectCode}>{subjectName}</Option>
                ))}
              </Select>
            </Descriptions.Item>
            <Descriptions.Item label={t('program.criteria.column5')}>
              <input
                type="text"
                className="form-control input-text"
                value={memo}
                onChange={(e) => setMemo(e.target.value)}
              />
            </Descriptions.Item>
            <Descriptions.Item label={t('program.criteria.column4')}>
              <Radio
                checked={useYN === 'Y'}
                onChange={() => setUseYN('Y')}
                text={t('common.option.yes')}
              />
              <Radio
                className="ml-4"
                checked={useYN === 'N'}
                onChange={() => setUseYN('N')}
                text={t('common.option.no')}
              />
            </Descriptions.Item>
          </Descriptions>
        </div>
      </Modal>
    </>
  );
};

export default Level2Content;
